import { StaffQuotationDetail, StaffQuotationLine } from '../services/staff-quotation.service';

export type QuotationFilter = 'ALL' | 'SUBMITTED' | 'UNDER_REVIEW' | 'PUNCHED' | 'REJECTED';

export interface QuotationKpiStats {
    total: number;
    submitted: number;
    underReview: number;
    punched: number;
    rejected: number;
}

export interface QuotationSeedLine {
    itemNumber: string;
    itemName: string;
    quantity: number;
    rate?: number;
}

export function computeQuotationKpis(rows: { status?: string }[]): QuotationKpiStats {
    let submitted = 0;
    let underReview = 0;
    let punched = 0;
    let rejected = 0;

    for (const row of rows) {
        const status = (row.status || '').toUpperCase();
        if (status === 'SUBMITTED') submitted++;
        else if (status === 'UNDER_REVIEW') underReview++;
        else if (status === 'PUNCHED') punched++;
        else if (status === 'REJECTED') rejected++;
    }

    return { total: rows.length, submitted, underReview, punched, rejected };
}

export function statusLabel(status: string): string {
    switch ((status || '').toUpperCase()) {
        case 'UNDER_REVIEW':
            return 'Under Review';
        case 'PUNCHED':
            return 'Punched';
        case 'REJECTED':
            return 'Rejected';
        case 'SUBMITTED':
        default:
            return 'Submitted';
    }
}

export function statusCssClass(status: string): string {
    switch ((status || '').toUpperCase()) {
        case 'UNDER_REVIEW':
            return 'status-review';
        case 'PUNCHED':
            return 'status-approved';
        case 'REJECTED':
            return 'status-rejected';
        case 'SUBMITTED':
        default:
            return 'status-submitted';
    }
}

export function matchesQuotationFilter(status: string | undefined, filter: QuotationFilter): boolean {
    if (filter === 'ALL') return true;
    return (status || '').toUpperCase() === filter;
}

export function matchesQuotationSearch(
    row: { quotationNumber?: string; customerId?: string; customerName?: string; punchedOrderNumber?: string },
    query: string
): boolean {
    const q = (query || '').trim().toLowerCase();
    if (!q) return true;
    return [row.quotationNumber, row.customerId, row.customerName, row.punchedOrderNumber]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
}

export function matchesQuotationDateRange(createdAt: string | undefined, fromDate: string, toDate: string): boolean {
    if (!createdAt) return !fromDate && !toDate;
    const day = createdAt.slice(0, 10);
    if (fromDate && day < fromDate) return false;
    if (toDate && day > toDate) return false;
    return true;
}

export function defaultQuotationDateRange(): { fromDate: string; toDate: string } {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - 30);
    const fmt = (d: Date) => d.toISOString().slice(0, 10);
    return { fromDate: fmt(from), toDate: fmt(to) };
}

export function canPunchQuotation(detail: StaffQuotationDetail | null): boolean {
    if (!detail) return false;
    const status = (detail.status || '').toUpperCase();
    return (status === 'SUBMITTED' || status === 'UNDER_REVIEW') && !detail.punchedOrderNumber;
}

export function canRejectQuotation(detail: StaffQuotationDetail | null): boolean {
    return canPunchQuotation(detail);
}

export function toSeedLines(lines: StaffQuotationLine[]): QuotationSeedLine[] {
    return (lines || [])
        .filter((l) => l.itemNumber && Number(l.qty) > 0)
        .map((l) => ({
            itemNumber: l.itemNumber,
            itemName: l.itemName || '',
            quantity: Number(l.qty),
            rate: l.rate
        }));
}
